<template>
    <b-card>
        <div id="report-body" class="w-100">
            <div class="mt-3" v-if="hasReopenedInvoices">
                <ArInvoiceNote show @refresh="$emit('refresh')" />
            </div>

            <!-- Table 1: Accounts Receivable -->
            <QTable
                :columns="columns.accReceivable"
                :data="tableData.accReceivable"
                hide-bottom
                :rows-per-page-options="[0]"
                style="width: 50%;"
                row-key="desc"
            >
                <template v-slot:body-cell-desc="props">
                    <td :class="tdClass(props)">
                        {{ props.row.desc }}
                    </td>
                </template>
                <template v-slot:body-cell-value="props">
                    <td :class="tdClass(props)">
                        {{ _toCurrency(props.row.value) }}
                    </td>
                </template>
            </QTable>
            <div class="font-weight-bold mb-5" style="margin-left: 16px;">
                Change in AR displayed as following: invoiced amount -
                <a href="#" v-scroll-to="'#payment'">¹payment</a> -
                <a href="#" v-scroll-to="'#credit'">²credit</a> +
                <a href="#" v-scroll-to="'#refund'">³refund</a> - write off
            </div>

            <!-- Table 2: Invoiced by Provider -->
            <QTable
                :columns="columns.invoicedByProvider"
                :data="tableData.invoicedByProvider"
                hide-bottom
                class="mb-4"
                :rows-per-page-options="[0]"
                row-key="provider"
            >
                <template v-slot:body-cell="props">
                    <td
                        v-if="props.col.field === 'discount'"
                        :class="tdClass(props)"
                    >
                        ({{ _toCurrency(props.value) }})
                    </td>
                    <td
                        v-else-if="props.col.field !== 'provider'"
                        :class="tdClass(props)"
                    >
                        {{ _toCurrency(props.value) }}
                    </td>
                    <td v-else :class="{ total: props.row.isTotal }">
                        {{ props.value }}
                    </td>
                </template>
            </QTable>

            <!-- Table 3: Payments -->
            <QTable
                :columns="columns.payments"
                :data="tableData.payments"
                hide-bottom
                class="mb-5"
                :rows-per-page-options="[0]"
                style="width: 50%;"
                row-key="desc"
            >
                <template v-slot:body-cell="props">
                    <td
                        v-if="props.col.field === 'payment_amount'"
                        :class="tdClass(props)"
                    >
                        {{ _toCurrency(props.value) }}
                    </td>
                    <td
                        v-else
                        :class="tdClass(props)"
                        :id="props.row.htmlId || ''"
                    >
                        <span v-if="props.row.tooltip">
                            {{ props.value }}
                            <i
                                v-b-tooltip.hover.top
                                :title="props.row.tooltip"
                                class="cus-information"
                                style="margin-top: 1px;"
                            ></i>
                        </span>
                        <span v-else>
                            {{ props.value }}
                        </span>
                    </td>
                </template>
            </QTable>

            <!-- Table 4: Credits -->
            <QTable
                :columns="columns.credits"
                :data="tableData.credits"
                hide-bottom
                class="mb-5"
                :rows-per-page-options="[0]"
                style="width: 50%;"
                row-key="desc"
            >
                <template v-slot:body-cell="props">
                    <td
                        v-if="props.col.field === 'credit_amount'"
                        :class="tdClass(props)"
                    >
                        {{ _toCurrency(props.value) }}
                    </td>
                    <td
                        v-else
                        :class="tdClass(props)"
                        :id="props.row.htmlId || ''"
                    >
                        <span v-if="props.row.tooltip">
                            {{ props.value }}
                            <i
                                v-b-tooltip.hover.top
                                :title="props.row.tooltip"
                                class="cus-information"
                                style="margin-top: 1px;"
                            ></i>
                        </span>
                        <span v-else>
                            {{ props.value }}
                        </span>
                    </td>
                </template>
            </QTable>

            <!-- Table 5: Refunds -->
            <QTable
                :columns="columns.refunds"
                :data="tableData.refunds"
                hide-bottom
                class="mb-2"
                :rows-per-page-options="[0]"
                style="width: 50%;"
                row-key="desc"
            >
                <template v-slot:body-cell="props">
                    <td
                        v-if="props.col.field === 'refund_amount'"
                        :class="tdClass(props)"
                    >
                        {{ _toCurrency(props.value) }}
                    </td>
                    <td
                        v-else
                        :class="tdClass(props)"
                        :id="props.row.htmlId || ''"
                    >
                        <span v-if="props.row.tooltip">
                            {{ props.value }}
                            <i
                                v-b-tooltip.hover.top
                                :title="props.row.tooltip"
                                class="cus-information"
                                style="margin-top: 1px;"
                            ></i>
                        </span>
                        <span v-else>
                            {{ props.value }}
                        </span>
                    </td>
                </template>
            </QTable>

            <!-- Table 6: Write offs -->
            <QTable
                :columns="columns.writeoff"
                :data="tableData.writeoff"
                hide-bottom
                class="mb-1"
                :rows-per-page-options="[0]"
                style="width: 50%;"
                row-key="desc"
            >
                <template v-slot:body-cell="props">
                    <td v-if="props.col.field === 'writeoff_amount'">
                        <b>
                            {{ _toCurrency(props.value) }}
                        </b>
                    </td>
                    <td v-else>
                        <b>{{ props.value }}</b>
                    </td>
                </template>
            </QTable>
        </div>
    </b-card>
</template>

<script>
import ArInvoiceNote from "@/components/elements/ar-note";
import { QTable } from "quasar";
import "@/css/quasar-table.css";

export default {
    components: {
        QTable,
        ArInvoiceNote
    },
    props: {
        tableData: {
            type: Object
        },
        hasReopenedInvoices: {
            type: Boolean
        }
    },
    data() {
        return {
            columns: {
                accReceivable: [
                    {
                        name: "desc",
                        field: "desc",
                        label: "Accounts Receivable",
                        align: "left",
                        headerStyle: "width:500px; font-weight:bold;"
                    },
                    {
                        name: "value",
                        field: "value",
                        label: "",
                        align: "left"
                    }
                ],
                invoicedByProvider: [
                    {
                        name: "provider",
                        field: "provider",
                        label: "Invoiced by Provider",
                        align: "left",
                        headerStyle: "width:500px; font-weight:bold;"
                    },
                    {
                        name: "listPrice",
                        label: "List Price",
                        field: "list_price",
                        align: "left",
                        headerStyle: "font-weight:bold;"
                    },
                    {
                        name: "discount",
                        label: "Discounts",
                        field: "discount",
                        align: "left",
                        headerStyle: "font-weight:bold;"
                    },
                    {
                        name: "taxes",
                        label: "Taxes",
                        field: "tax",
                        align: "left",
                        headerStyle: "font-weight:bold;"
                    },
                    {
                        name: "lateFees",
                        label: "Late Fees",
                        field: "late_fee",
                        align: "left",
                        headerStyle: "font-weight:bold;"
                    },
                    {
                        name: "total",
                        label: "Total",
                        field: "total",
                        align: "left",
                        headerStyle: "font-weight:bold;"
                    }
                ],
                payments: [
                    {
                        name: "name",
                        field: "payment_name",
                        label: "Payments",
                        align: "left",
                        headerStyle: "width:500px; font-weight:bold;"
                    },
                    {
                        name: "amount",
                        field: "payment_amount",
                        label: "",
                        align: "left"
                    }
                ],
                credits: [
                    {
                        name: "name",
                        field: "credit_type",
                        label: "Credits",
                        align: "left",
                        headerStyle: "width:500px; font-weight:bold;"
                    },
                    {
                        name: "amount",
                        field: "credit_amount",
                        label: "",
                        align: "left"
                    }
                ],
                refunds: [
                    {
                        name: "name",
                        field: "refund_type",
                        label: "Refunds",
                        align: "left",
                        headerStyle: "width:500px; font-weight:bold;"
                    },
                    {
                        name: "amount",
                        field: "refund_amount",
                        label: "",
                        align: "left"
                    }
                    /* {
                        name: "refunds",
                        field: "label",
                        label: "",
                        align: "left",
                        headerStyle: "width:500px; font-weight:bold;"
                    },
                    {
                        name: "amount",
                        field: "refund_amount",
                        label: "",
                        align: "left"
                    } */
                ],

                writeoff: [
                    {
                        name: "writeoff",
                        field: "label",
                        label: "",
                        align: "left",
                        headerStyle: "width:500px; font-weight:bold;"
                    },
                    {
                        name: "amount",
                        field: "writeoff_amount",
                        label: "",
                        align: "left"
                    }
                ]
            }
        };
    },
    methods: {
        tdClass(props) {
            return {
                total: props.row.isTotal,
                extendedTotal: props.row.isExtendedTotal
            };
        }
    }
};
</script>

<style scoped>
.report-heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    padding: 0 5px;
}
.report-heading span {
    text-align: right;
}
th {
    font-weight: bold;
    white-space: nowrap;
}
.card-body {
    padding-top: 0;
    padding-bottom: 0;
}
.report-description {
    display: block;
    margin-bottom: 10px;
    font-style: italic;
}
.total {
    font-weight: bold;
    border-top: 3px double #e4e4e4;
}
.extendedTotal {
    font-weight: bold;
    border-top: 0;
}
</style>
